/**
 * The look of a control whose add-on is bought but blocked by the license right now
 * (state 'locked', see lib/licenseFeatures.ts): disabled, a small lock and one translated
 * sentence ("Not included in your current licence, contact your administrator") as its
 * tooltip. aria-disabled rather than disabled, so the tooltip still shows and keyboard
 * users can reach the control and hear why.
 */
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { useLicenseFeature } from '../../hooks/useLicenseFeatures';
import type { FeatureState, LicenseFeature } from '../../lib/licenseFeatures';

export const LOCKED_CLASS = 'cursor-not-allowed opacity-50';

export function LicenseLockIcon({ className }: { className?: string }) {
  return <Lock className={clsx('h-3.5 w-3.5 flex-none', className)} aria-hidden="true" data-testid="license-lock" />;
}

export interface LicenseGate {
  state: FeatureState;
  hidden: boolean;
  locked: boolean;
  /** The translated reason, for tooltips and notices. */
  lockText: string;
  /** Spread onto a locked control (after its own title/aria-label): disabled look and reason. */
  attrs: { 'aria-disabled'?: true; title?: string; 'data-license-locked'?: 'true' };
  /** The handler, or a no-op while locked. */
  guard: <A extends unknown[]>(fn: (...args: A) => unknown) => (...args: A) => void;
}

/** One control's license gate; several features: all are needed (mail AI = 'ai' + 'mail'). */
export function useLicenseGate(...features: LicenseFeature[]): LicenseGate {
  const { t } = useTranslation();
  const state = useLicenseFeature(...features);
  const locked = state === 'locked';
  const lockText = t('license.locked');
  return {
    state,
    hidden: state === 'hidden',
    locked,
    lockText,
    attrs: locked ? { 'aria-disabled': true, title: lockText, 'data-license-locked': 'true' } : {},
    guard: (fn) => (...args) => { if (!locked) fn(...args); },
  };
}

/** Inline notice where a whole area is locked (e.g. a panel). */
export function LicenseLockNotice({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <p role="note" className={clsx('flex items-center gap-1.5 text-xs text-nord-muted', className)}>
      <LicenseLockIcon />
      <span>{t('license.locked')}</span>
    </p>
  );
}
